import React, { useState, useCallback } from 'react';
import { Input, Button, Tooltip } from 'antd';
import {
  SearchOutlined,
  CloseCircleOutlined,
  TagsOutlined,
  TagOutlined
} from '@ant-design/icons';

const { TextArea } = Input;

const SearchInput = ({
  value,
  onChange,
  onSearch,
  onClear,
  placeholder,
  textareaPlaceholder,
  className,
  style
}) => {
  const [isTextAreaMode, setIsTextAreaMode] = useState(false);

  const safeOnClear = useCallback(() => {
    if (typeof onClear === 'function') {
      onClear();
      return;
    }
    if (typeof onChange === 'function') {
      onChange('');
    }
  }, [onClear, onChange]);

  // Helper function to convert between comma-separated and newline-separated formats
  const convertSearchText = useCallback((text, toNewlines = true) => {
    if (!text) return '';

    if (toNewlines) {
      // Convert comma-separated to newline-separated
      return text
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0)
        .join('\n');
    }

    // Convert newline-separated to comma-separated
    return text
      .split('\n')
      .map((item) => item.trim())
      .filter((item) => item.length > 0)
      .join(',');
  }, []);

  // Get search text in comma-separated format for API calls (does not modify display text)
  const getSearchTextForAPI = useCallback(
    (text) => {
      if (!text) return '';
      if (text.includes('\n')) {
        return convertSearchText(text, false);
      }
      return text;
    },
    [convertSearchText]
  );

  // Handle paste events to detect Google Sheets data
  const handlePaste = useCallback(
    (e) => {
      const pastedText = e.clipboardData.getData('text');

      // Check if pasted text contains newlines (likely from Google Sheets column copy)
      if (pastedText && pastedText.includes('\n')) {
        e.preventDefault();

        const cleanedLines = pastedText
          .split('\n')
          .map((line) => line.trim())
          .filter((line) => line.length > 0);

        if (cleanedLines.length > 1) {
          setIsTextAreaMode(true);
          const formattedText = cleanedLines.join('\n');
          onChange(formattedText);
        } else if (cleanedLines.length === 1) {
          onChange(cleanedLines[0]);
        }
      }
    },
    [onChange]
  );

  const handleInputChange = useCallback(
    (e) => {
      const inputValue = e.target.value;

      if (!isTextAreaMode && inputValue.includes('\n')) {
        setIsTextAreaMode(true);
      }

      onChange(inputValue);
    },
    [onChange, isTextAreaMode]
  );

  // Toggle between textarea and input modes
  const toggleSearchMode = useCallback(() => {
    const currentText = value || '';
    const convertedText = isTextAreaMode
      ? convertSearchText(currentText, false)
      : convertSearchText(currentText, true);

    setIsTextAreaMode(!isTextAreaMode);
    onChange(convertedText);
  }, [isTextAreaMode, value, convertSearchText, onChange]);

  // Handle search - convert to comma-separated for API but keep display text unchanged
  const handleSearch = useCallback(() => {
    const apiSearchText = getSearchTextForAPI(value);
    onSearch(apiSearchText);
  }, [value, onSearch, getSearchTextForAPI]);

  // Handle keyboard events
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && e.shiftKey) {
        // Shift+Enter handled by global listener in filter component
        e.preventDefault();
        return;
      }

      if (e.key === 'Enter' && !e.shiftKey && !isTextAreaMode) {
        e.preventDefault();
        const apiSearchText = getSearchTextForAPI(value);
        onSearch(apiSearchText);
      }
    },
    [isTextAreaMode, value, onSearch, getSearchTextForAPI]
  );

  const handleClear = useCallback(() => {
    setIsTextAreaMode(false);
    safeOnClear();
  }, [safeOnClear]);

  return (
    <div style={{ position: 'relative', ...style }} className={className}>
      {isTextAreaMode ? (
        <TextArea
          placeholder={
            textareaPlaceholder ||
            'Search by order name, email, SKU, product title (one per line or use commas)'
          }
          value={value}
          onChange={handleInputChange}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
          style={{
            width: '100%',
            backgroundColor: 'transparent',
            resize: 'vertical',
            minHeight: 80
          }}
          variant='underlined'
          autoSize={{ minRows: 3, maxRows: 8 }}
        />
      ) : (
        <Input
          prefix={<SearchOutlined style={{ color: '#999' }} />}
          placeholder={
            placeholder ||
            'Search by order name, email, SKU, product title (use commas for multiple values)'
          }
          value={value}
          onChange={handleInputChange}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
          style={{ width: '100%', backgroundColor: 'transparent' }}
          variant='underlined'
          size='middle'
        />
      )}

      {/* Search button for textarea mode (since Enter doesn't work) */}
      {isTextAreaMode && value && (
        <Button
          type='text'
          size='small'
          icon={<SearchOutlined />}
          onClick={handleSearch}
          style={{
            position: 'absolute',
            right: value ? 56 : 32,
            top: 8,
            color: '#999',
            padding: 0,
            minWidth: 'auto',
            zIndex: 1,
            backgroundColor: '#f0f0f0',
            borderRadius: '4px'
          }}
          title='Search now'
        />
      )}

      {/* Toggle button for switching between input and textarea */}
      <Button
        type='text'
        size='small'
        icon={
          isTextAreaMode ? (
            <TagOutlined />
          ) : (
            <Tooltip title='Switch to multi-line input'>
              <TagsOutlined />
            </Tooltip>
          )
        }
        onClick={toggleSearchMode}
        style={{
          position: 'absolute',
          right: value ? 32 : 8,
          top: isTextAreaMode ? 8 : '50%',
          transform: isTextAreaMode ? 'none' : 'translateY(-50%)',
          padding: 0,
          color: '#999',
          minWidth: 'auto',
          zIndex: 1,
          backgroundColor: '#f0f0f0',
          borderRadius: '4px'
        }}
        title={isTextAreaMode ? 'Switch to single line input' : 'Switch to multi-line input'}
      />

      {/* Clear button */}
      {value && (
        <Button
          type='text'
          size='small'
          icon={<CloseCircleOutlined />}
          onClick={handleClear}
          style={{
            position: 'absolute',
            right: 8,
            top: isTextAreaMode ? 8 : '50%',
            transform: isTextAreaMode ? 'none' : 'translateY(-50%)',
            color: '#999',
            padding: 0,
            minWidth: 'auto',
            zIndex: 1,
            backgroundColor: '#f0f0f0',
            borderRadius: '4px'
          }}
          title='Clear search and reload'
        />
      )}
    </div>
  );
};

export default SearchInput;
